import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, getDocs, orderBy, query, serverTimestamp, where } from 'firebase/firestore';
import Navbar from './Navbar';
import Seo from '../components/Seo';
import { db } from '../firebaseConfig';
import '../styles/berbagi.css';

const isImageUrl = (url = '') => {
  return /\.(jpeg|jpg|png|webp|gif)$/i.test(url) || url.includes('images.unsplash.com');
};

const getYoutubeEmbed = (url = '') => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu.be')) {
      const id = parsed.pathname.replace('/', '');
      return id ? `https://www.youtube.com/embed/${id}` : '';
    }
    if (parsed.hostname.includes('youtube.com')) {
      const id = parsed.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : '';
    }
  } catch (err) {
    return '';
  }
  return '';
};

export default function Berbagi() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', city: '', story: '', mediaUrl: '' });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchStories = async () => {
    try {
      const storyQuery = query(
        collection(db, 'stories'),
        where('status', '==', 'approved'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(storyQuery);
      setStories(snap.docs.map(docItem => ({ id: docItem.id, ...docItem.data() })));
    } catch (err) {
      try {
        const fallbackSnap = await getDocs(collection(db, 'stories'));
        const approved = fallbackSnap.docs
          .map(docItem => ({ id: docItem.id, ...docItem.data() }))
          .filter(item => item.status === 'approved')
          .sort((a, b) => {
            const aTime = a.createdAt?.seconds || 0;
            const bTime = b.createdAt?.seconds || 0;
            return bTime - aTime;
          });
        setStories(approved);
      } catch (fallbackErr) {
        setError('Gagal memuat cerita.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStories();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!form.story.trim() || form.story.trim().length < 20) {
      setError('Cerita terlalu singkat. Minimal 20 karakter.');
      return;
    }
    if (!consent) {
      setError('Mohon centang persetujuan publikasi.');
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'stories'), {
        name: form.name.trim(),
        city: form.city.trim(),
        story: form.story.trim(),
        mediaUrl: form.mediaUrl.trim(),
        consent: true,
        status: 'pending',
        createdAt: serverTimestamp()
      });
      setForm({ name: '', city: '', story: '', mediaUrl: '' });
      setConsent(false);
      setMessage('Terima kasih. Cerita Anda sudah kami terima dan akan ditinjau admin.');
    } catch (err) {
      setError('Gagal mengirim cerita. Silakan coba lagi.');
    } finally {
      setSubmitting(false);
    }
  };

  const storyCards = useMemo(() => {
    return stories.map(item => {
      const displayName = item.name?.trim() ? item.name : 'Hamba Allah';
      const displayCity = item.city?.trim() ? item.city : 'Indonesia';
      const createdAt =
        item.createdAt?.seconds
          ? new Date(item.createdAt.seconds * 1000).toLocaleDateString('id-ID')
          : '';
      const mediaUrl = item.mediaUrl?.trim();
      const youtubeEmbed = mediaUrl ? getYoutubeEmbed(mediaUrl) : '';

      return (
        <article key={item.id} className="story-card">
          <div className="story-header">
            <div>
              <h3>{displayName}</h3>
              <span>{displayCity}</span>
            </div>
            {createdAt && <small>{createdAt}</small>}
          </div>
          <p className="story-text">{item.story}</p>
          {mediaUrl && (
            <div className="story-media">
              {youtubeEmbed ? (
                <div className="story-video">
                  <iframe
                    src={youtubeEmbed}
                    title={`Cerita ${displayName}`}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : isImageUrl(mediaUrl) ? (
                <img src={mediaUrl} alt={`Media cerita ${displayName}`} loading="lazy" />
              ) : (
                <a href={mediaUrl} target="_blank" rel="noreferrer" className="btn btn-outline-success btn-sm">
                  Lihat Media
                </a>
              )}
            </div>
          )}
        </article>
      );
    });
  }, [stories]);

  return (
    <div className="story-page">
      <Seo
        title="Berbagi Cerita"
        description="Bagikan pengalaman ruqyah syar'i dan baca kisah-kisah yang sudah disetujui admin."
        canonical="/berbagi"
      />
      <Navbar />

      <section className="story-hero">
        <div className="container">
          <span className="story-badge">Berbagi Cerita</span>
          <h1>Bagikan Pengalaman Ruqyah Anda</h1>
          <p>
            Cerita Anda bisa menjadi penguat bagi orang lain. Semua cerita akan ditinjau admin sebelum ditampilkan.
          </p>
          <a href="#form-cerita" className="btn btn-success">
            Bagikan Cerita Anda
          </a>
        </div>
      </section>

      <section className="story-list-section">
        <div className="container">
          <div className="section-head">
            <div>
              <h2>Cerita yang Sudah Disetujui</h2>
              <p>Kisah-kisah inspiratif dari pasien dan keluarga.</p>
            </div>
          </div>

          {loading && <p>Memuat cerita...</p>}
          {!loading && storyCards.length === 0 && (
            <p className="text-muted">Belum ada cerita yang ditampilkan.</p>
          )}

          <div className="story-grid">{storyCards}</div>
        </div>
      </section>

      <section className="story-form-section" id="form-cerita">
        <div className="container">
          <div className="form-card">
            <div className="form-header">
              <h2>Bagikan Cerita Anda</h2>
              <p>Cerita akan tampil setelah disetujui admin. Informasi sensitif tidak akan dipublikasikan.</p>
            </div>

            {message && <div className="alert alert-success">{message}</div>}
            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleSubmit} className="story-form">
              <div className="form-row">
                <div>
                  <label>Nama (opsional)</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Contoh: Ahmad S."
                  />
                </div>
                <div>
                  <label>Kota (opsional)</label>
                  <input
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    placeholder="Contoh: Tegal"
                  />
                </div>
              </div>

              <label>Cerita Anda *</label>
              <textarea
                name="story"
                value={form.story}
                onChange={handleChange}
                rows="6"
                maxLength={1200}
                placeholder="Ceritakan pengalaman Anda secara singkat dan jelas..."
                required
              />

              <label>URL Foto/Video (opsional)</label>
              <input
                type="url"
                name="mediaUrl"
                value={form.mediaUrl}
                onChange={handleChange}
                placeholder="https://..."
              />
              <div className="form-helper">
                Upload file langsung belum tersedia. Silakan gunakan URL publik (Google Drive/YouTube/Imgur).
              </div>

              <div className="form-check">
                <input
                  type="checkbox"
                  id="consent"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                />
                <label htmlFor="consent">
                  Saya setuju cerita ini ditinjau admin dan dapat dipublikasikan.
                </label>
              </div>

              <button type="submit" className="btn btn-success" disabled={submitting}>
                {submitting ? 'Mengirim...' : 'Kirim Cerita'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
